import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type UiMode = 'default' | 'operator';
type TooltipStyle = 'default' | 'light';
export type MarkerSize = 'xs' | 's' | 'm';

interface UiModeState {
  uiMode: UiMode;
  tooltipStyle: TooltipStyle;
  loaderColor: string | null;
  logoBgColor: string | null; // operator-logo background override (falls back to theme primary, then #000000)
  // Marker customization (inbound-only via URL params, not persisted, not round-tripped)
  // Per-status ring/pin colors (override the hardcoded RGB stops in getConicGradient)
  markerColorAvailable: string | null; // green segment + green single-pin border
  markerColorBusy: string | null; // orange segment + orange single-pin border
  markerColorDisconnected: string | null; // grey segment + grey single-pin border
  markerColorError: string | null; // red segment + red single-pin border
  markerColor: string | null; // center/count circle background accent
  markerSize: MarkerSize | null; // marker size override (null/'md' = count-based)
  countryHighlightColor: string | null; // country-overlay hover highlight (overrides operator brand color)
  countryFillColor: string | null; // country-overlay resting fill tint (hoverable, non-highlighted)
  countryFillOpacity: number | null; // country-overlay resting fill opacity (0–1)
  setUiMode: (mode: UiMode) => void;
  setTooltipStyle: (style: TooltipStyle) => void;
  setLoaderColor: (color: string | null) => void;
  setLogoBgColor: (color: string | null) => void;
  setMarkerColorAvailable: (color: string | null) => void;
  setMarkerColorBusy: (color: string | null) => void;
  setMarkerColorDisconnected: (color: string | null) => void;
  setMarkerColorError: (color: string | null) => void;
  setMarkerColor: (color: string | null) => void;
  setMarkerSize: (size: MarkerSize | null) => void;
  setCountryHighlightColor: (color: string | null) => void;
  setCountryFillColor: (color: string | null) => void;
  setCountryFillOpacity: (opacity: number | null) => void;
}

export const useUiModeStore = create<UiModeState>()(
  devtools(
    (set) => ({
      uiMode: 'default',
      tooltipStyle: 'default',
      loaderColor: null,
      logoBgColor: null,
      markerColorAvailable: null,
      markerColorBusy: null,
      markerColorDisconnected: null,
      markerColorError: null,
      markerColor: null,
      markerSize: 's', // default to small markers; ?marker_size=m → count-based
      countryHighlightColor: null,
      countryFillColor: null,
      countryFillOpacity: null,
      setUiMode: (mode) => set({ uiMode: mode }),
      setTooltipStyle: (style) => set({ tooltipStyle: style }),
      setLoaderColor: (color) => set({ loaderColor: color }),
      setLogoBgColor: (color) => set({ logoBgColor: color }),
      setMarkerColorAvailable: (color) => set({ markerColorAvailable: color }),
      setMarkerColorBusy: (color) => set({ markerColorBusy: color }),
      setMarkerColorDisconnected: (color) =>
        set({ markerColorDisconnected: color }),
      setMarkerColorError: (color) => set({ markerColorError: color }),
      setMarkerColor: (color) => set({ markerColor: color }),
      setMarkerSize: (size) => set({ markerSize: size }),
      setCountryHighlightColor: (color) =>
        set({ countryHighlightColor: color }),
      setCountryFillColor: (color) => set({ countryFillColor: color }),
      setCountryFillOpacity: (opacity) => set({ countryFillOpacity: opacity }),
    }),
    { name: 'UiModeStore' }
  )
);
